import React, { useCallback, useEffect, useState } from 'react';

import type { QuotaQueueInfo } from '../../utils/quotaQueue';
import { type QuotaQueueAction, QuotaQueueNotice } from '../QuotaQueueNotice';

export const CHAT_QUOTA_QUEUED_EVENT = 'chat_quota_queued';
/** Fired when a queued automatic reply may be asked for again (its message is already saved). */
export const CHAT_QUOTA_RETRIGGER_EVENT = 'chat_quota_retrigger';

/** A message the API answered "queued for your plan's hourly quota": the user sends it again once the quota resets. */
export interface QueuedChatMessage {
  info: QuotaQueueInfo;
  /** The message to send again; empty for a reply to a message that is already saved. */
  content: string;
  projectId: string;
  /** The chat session it belongs to: it is only shown and sent there. */
  sessionId?: string | null;
  mediaUrls?: string[];
  /** ``reply``: the message is saved and only its automatic reply waits. */
  kind?: 'send' | 'reply';
}

export const dispatchChatQuotaQueued = (detail: QueuedChatMessage): void => {
  window.dispatchEvent(new globalThis.CustomEvent(CHAT_QUOTA_QUEUED_EVENT, { detail }));
};

const preview = (content: string): string => (content.length > 90 ? `${content.slice(0, 87)}…` : content);

/** A second message sent while one waits joins it, so neither is lost; a new send also answers a waiting reply. */
export const mergeQueued = (current: QueuedChatMessage | null, next: QueuedChatMessage): QueuedChatMessage => {
  if (!current || current.kind === 'reply' || next.kind === 'reply' || current.sessionId !== next.sessionId) return next;

  return {
    ...next,
    content: `${current.content}\n\n${next.content}`,
    mediaUrls: [...(current.mediaUrls ?? []), ...(next.mediaUrls ?? [])],
  };
};

const useQueuedMessage = (projectId?: string | null) => {
  const [queued, setQueued] = useState<QueuedChatMessage | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as globalThis.CustomEvent<QueuedChatMessage>).detail;

      if (detail?.projectId && detail.projectId === projectId) setQueued((current) => mergeQueued(current, detail));
    };

    window.addEventListener(CHAT_QUOTA_QUEUED_EVENT, handler);

    return () => window.removeEventListener(CHAT_QUOTA_QUEUED_EVENT, handler);
  }, [projectId]);

  return [queued, setQueued] as const;
};

const release = (queued: QueuedChatMessage): void => {
  if (queued.kind === 'reply') {
    const detail = { projectId: queued.projectId, sessionId: queued.sessionId ?? null };

    window.dispatchEvent(new globalThis.CustomEvent(CHAT_QUOTA_RETRIGGER_EVENT, { detail }));

    return;
  }
  const detail = { content: queued.content, mediaUrls: queued.mediaUrls };

  window.dispatchEvent(new globalThis.CustomEvent('submit_user_message', { detail }));
};

/**
 * Nothing sends the queued message by itself (a spent quota would keep re-queueing it): the user presses Send now,
 * never while a reply is streaming. Still spent, the API refuses again and the message is queued with the new wait.
 */
const sendNow = (queued: QueuedChatMessage, clear: () => void, busy: boolean): QuotaQueueAction => ({
  label: queued.kind === 'reply' ? 'Try again' : 'Send now',
  busy,
  onClick: () => {
    clear();
    release(queued);
  },
});

const QueuedLine: React.FC<{ queued: QueuedChatMessage; onEdit: () => void }> = ({ queued, onEdit }) => (
  <div className="chat-quota-queue__message">
    {queued.kind === 'reply'
      ? <span className="chat-quota-queue__preview">Your message is saved; its reply is queued.</span>
      : <span className="chat-quota-queue__preview">Queued: “{preview(queued.content)}”</span>}
    {queued.kind !== 'reply' && (
      <button type="button" className="chat-quota-queue__edit" onClick={onEdit}>Edit instead</button>
    )}
  </div>
);

interface ChatQuotaQueueProps {
  projectId?: string | null;
  /** The open chat session: a message queued in another session waits until that one is open again. */
  sessionId?: string | null;
  /** A reply is streaming: the queued message cannot be sent until it finishes. */
  busy?: boolean;
}

export const ChatQuotaQueue: React.FC<ChatQuotaQueueProps> = ({ projectId, sessionId, busy = false }) => {
  const [queued, setQueued] = useQueuedMessage(projectId);
  const clear = useCallback(() => setQueued(null), [setQueued]);
  const here = queued && (!queued.sessionId || !sessionId || queued.sessionId === sessionId) ? queued : null;
  const editInstead = () => {
    clear();
    if (here) window.dispatchEvent(new globalThis.CustomEvent('prefill_chat_input', { detail: { content: here.content } }));
  };

  if (!here) return null;

  return (
    <div className="chat-quota-queue" aria-label="Queued message">
      <QuotaQueueNotice notice={here.info} action={sendNow(here, clear, busy)} />
      <QueuedLine queued={here} onEdit={editInstead} />
    </div>
  );
};
